// Copyright (c) 2024 - Present. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted

/**
 * This module defines user-facing labels, descriptions, and examples for each
 * ledger account type. Descriptions are JSX fragments to support rich formatting.
 */
import { LedgerAccountType } from '@/account/api/types';
import { Box, List, ListItem, Typography } from '@mui/material';

interface LedgerAccountLabel {
  label: string;
  description: React.ReactNode;
  example: string;
}

const LIST_ITEM_PADDING = 3;

/**
 * User-facing labels and descriptions for each ledger account type.
 * Used in the UI to provide context about what each account represents.
 */
export const LEDGER_ACCOUNT_LABELS: Record<
  LedgerAccountType,
  LedgerAccountLabel
> = {
  INV_INVENTORY: {
    label: 'Inventory',
    description: (
      <Box>
        <Typography variant="subtitle1" marginBottom={1}>
          Records finished goods or ready-to-deploy hardware units as an
          inventory asset balance.
        </Typography>
        <Typography variant="body1" fontStyle="italic" marginBottom={1}>
          Normal balance: debit
        </Typography>
        <Typography variant="body1">
          Appears in the following journal entries:
        </Typography>
        <List
          dense
          sx={{
            listStyleType: 'disc',
            listStylePosition: 'outside',
            paddingLeft: LIST_ITEM_PADDING,
          }}
        >
          <ListItem sx={{ display: 'list-item', padding: 0 }}>
            Inventory Purchases
          </ListItem>
          <ListItem sx={{ display: 'list-item', padding: 0 }}>
            Inventory Deployments (to Fixed Assets)
          </ListItem>
          <ListItem sx={{ display: 'list-item', padding: 0 }}>
            Inventory Sales
          </ListItem>
          <ListItem sx={{ display: 'list-item', padding: 0 }}>
            Inventory Scrap
          </ListItem>
        </List>
      </Box>
    ),
    example: '10300 Finished Goods',
  },
  INV_CLEARING: {
    label: 'Inventory clearing (GRNI/GRIR)',
    description: (
      <Box>
        <Typography variant="subtitle1" marginBottom={1}>
          Temporary liability balance for goods received without matching
          invoice records; clears when A/P bills are posted.
        </Typography>
        <Typography variant="body1" fontStyle="italic" marginBottom={1}>
          Normal balance: credit
        </Typography>
        <Typography variant="body1">
          Appears in the following journal entries:
        </Typography>
        <List
          dense
          sx={{
            listStyleType: 'disc',
            listStylePosition: 'outside',
            paddingLeft: LIST_ITEM_PADDING,
          }}
        >
          <ListItem sx={{ display: 'list-item', padding: 0 }}>
            Inventory Purchases
          </ListItem>
        </List>
      </Box>
    ),
    example: '24010 Inventory Clearing',
  },
  INV_COGS: {
    label: 'Cost of goods sold',
    description: (
      <Box>
        <Typography variant="subtitle1" marginBottom={1}>
          Recognizes the cost of inventory sold to customers on the income
          statement; used only for direct sales.
        </Typography>
        <Typography variant="body1" fontStyle="italic" marginBottom={1}>
          Normal balance: debit
        </Typography>
        <Typography variant="body1">
          Appears in the following journal entries:
        </Typography>
        <List
          dense
          sx={{
            listStyleType: 'disc',
            listStylePosition: 'outside',
            paddingLeft: LIST_ITEM_PADDING,
          }}
        >
          <ListItem sx={{ display: 'list-item', padding: 0 }}>
            Inventory Sales
          </ListItem>
        </List>
      </Box>
    ),
    example: '50010 Cost of Goods Sold',
  },
  INV_WRITE_OFF: {
    label: 'Inventory write-off expense',
    description: (
      <Box>
        <Typography variant="subtitle1" marginBottom={1}>
          Records the cost of scrapped, damaged, or obsolete inventory items on
          the income statement.
        </Typography>
        <Typography variant="body1" fontStyle="italic" marginBottom={1}>
          Normal balance: debit
        </Typography>
        <Typography variant="body1">
          Appears in the following journal entries:
        </Typography>
        <List
          dense
          sx={{
            listStyleType: 'disc',
            listStylePosition: 'outside',
            paddingLeft: LIST_ITEM_PADDING,
          }}
        >
          <ListItem sx={{ display: 'list-item', padding: 0 }}>
            Inventory Scrap
          </ListItem>
        </List>
      </Box>
    ),
    example: '62050 Inventory Write-off',
  },
  FA_FIXED_ASSETS: {
    label: 'Fixed assets',
    description: (
      <Box>
        <Typography variant="subtitle1" marginBottom={1}>
          Asset balance captures the cost basis of units placed in service and
          subject to depreciation.
        </Typography>
        <Typography variant="body1" fontStyle="italic" marginBottom={1}>
          Normal balance: debit
        </Typography>
        <Typography variant="body1">
          Appears in the following journal entries:
        </Typography>
        <List
          dense
          sx={{
            listStyleType: 'disc',
            listStylePosition: 'outside',
            paddingLeft: LIST_ITEM_PADDING,
          }}
        >
          <ListItem sx={{ display: 'list-item', padding: 0 }}>
            Inventory Deployments (to Fixed Assets)
          </ListItem>
          <ListItem sx={{ display: 'list-item', padding: 0 }}>
            Fixed Asset Sales
          </ListItem>
          <ListItem sx={{ display: 'list-item', padding: 0 }}>
            Fixed Asset Scrap
          </ListItem>
        </List>
      </Box>
    ),
    example: '15000 Fixed Assets',
  },
  FA_CAPITALIZED_DEPLOYMENT: {
    label: 'Capitalized deployment costs',
    description: (
      <Box>
        <Typography variant="subtitle1" marginBottom={1}>
          Offsets a portion of operating costs that are capitalized into fixed
          assets; acts as a credit-normal expense account.
        </Typography>
        <Typography variant="body1" fontStyle="italic" marginBottom={1}>
          Normal balance: credit
        </Typography>
        <Typography variant="body1">
          Appears in the following journal entries:
        </Typography>
        <List
          dense
          sx={{
            listStyleType: 'disc',
            listStylePosition: 'outside',
            paddingLeft: LIST_ITEM_PADDING,
          }}
        >
          <ListItem sx={{ display: 'list-item', padding: 0 }}>
            Inventory Deployments (to Fixed Assets)
          </ListItem>
        </List>
      </Box>
    ),
    example: '15020 Deployment Costs',
  },
  FA_DEPRECIATION: {
    label: 'Depreciation expense',
    description: (
      <Box>
        <Typography variant="subtitle1" marginBottom={1}>
          Periodic depreciation recognized for fixed assets in service on the
          income statement.
        </Typography>
        <Typography variant="body1" fontStyle="italic" marginBottom={1}>
          Normal balance: debit
        </Typography>
        <Typography variant="body1">
          Appears in the following journal entries:
        </Typography>
        <List
          dense
          sx={{
            listStyleType: 'disc',
            listStylePosition: 'outside',
            paddingLeft: LIST_ITEM_PADDING,
          }}
        >
          <ListItem sx={{ display: 'list-item', padding: 0 }}>
            Fixed Asset Depreciation
          </ListItem>
        </List>
      </Box>
    ),
    example: '62010 Depreciation Expense',
  },
  FA_ACCUMULATED_DEPRECIATION: {
    label: 'Accumulated depreciation',
    description: (
      <Box>
        <Typography variant="subtitle1" marginBottom={1}>
          Cumulative depreciation taken on fixed assets; contra-asset balance
          that offsets the cost basis to calculate net book value.
        </Typography>
        <Typography variant="body1" fontStyle="italic" marginBottom={1}>
          Normal balance: credit
        </Typography>
        <Typography variant="body1">
          Appears in the following journal entries:
        </Typography>
        <List
          dense
          sx={{
            listStyleType: 'disc',
            listStylePosition: 'outside',
            paddingLeft: LIST_ITEM_PADDING,
          }}
        >
          <ListItem sx={{ display: 'list-item', padding: 0 }}>
            Fixed Asset Depreciation
          </ListItem>
          <ListItem sx={{ display: 'list-item', padding: 0 }}>
            Fixed Asset Sales
          </ListItem>
          <ListItem sx={{ display: 'list-item', padding: 0 }}>
            Fixed Asset Scrap
          </ListItem>
        </List>
      </Box>
    ),
    example: '15100 Accumulated Depreciation',
  },
  FA_DISPOSAL_CLEARING: {
    label: 'Asset disposal clearing',
    description: (
      <Box>
        <Typography variant="subtitle1" marginBottom={1}>
          Temporary asset balance used during sale or scrapping of fixed assets;
          clears when A/R invoices are posted and should net to zero once
          transactions are complete.
        </Typography>
        <Typography variant="body1" fontStyle="italic" marginBottom={1}>
          Normal balance: debit
        </Typography>
        <Typography variant="body1">
          Appears in the following journal entries:
        </Typography>
        <List
          dense
          sx={{
            listStyleType: 'disc',
            listStylePosition: 'outside',
            paddingLeft: LIST_ITEM_PADDING,
          }}
        >
          <ListItem sx={{ display: 'list-item', padding: 0 }}>
            Fixed Asset Sales
          </ListItem>
          <ListItem sx={{ display: 'list-item', padding: 0 }}>
            Fixed Asset Scrap
          </ListItem>
        </List>
      </Box>
    ),
    example: '15900 Asset Disposal Clearing',
  },
  FA_DISPOSAL_GAIN: {
    label: 'Gain on disposal',
    description: (
      <Box>
        <Typography variant="subtitle1" marginBottom={1}>
          Gains recognized on the income statement for sale or scrap of fixed
          assets when proceeds exceed book value.
        </Typography>
        <Typography variant="body1" fontStyle="italic" marginBottom={1}>
          Normal balance: credit
        </Typography>
        <Typography variant="body1">
          Appears in the following journal entries:
        </Typography>
        <List
          dense
          sx={{
            listStyleType: 'disc',
            listStylePosition: 'outside',
            paddingLeft: LIST_ITEM_PADDING,
          }}
        >
          <ListItem sx={{ display: 'list-item', padding: 0 }}>
            Fixed Asset Sales
          </ListItem>
          <ListItem sx={{ display: 'list-item', padding: 0 }}>
            Fixed Asset Scrap
          </ListItem>
        </List>
      </Box>
    ),
    example: '82010 Gain on Disposal',
  },
  FA_DISPOSAL_LOSS: {
    label: 'Loss on disposal',
    description: (
      <Box>
        <Typography variant="subtitle1" marginBottom={1}>
          Losses recognized on the income statement for sale or scrap of fixed
          assets when proceeds are less than book value.
        </Typography>
        <Typography variant="body1" fontStyle="italic" marginBottom={1}>
          Normal balance: debit
        </Typography>
        <Typography variant="body1">
          Appears in the following journal entries:
        </Typography>
        <List
          dense
          sx={{
            listStyleType: 'disc',
            listStylePosition: 'outside',
            paddingLeft: LIST_ITEM_PADDING,
          }}
        >
          <ListItem sx={{ display: 'list-item', padding: 0 }}>
            Fixed Asset Sales
          </ListItem>
          <ListItem sx={{ display: 'list-item', padding: 0 }}>
            Fixed Asset Scrap
          </ListItem>
        </List>
      </Box>
    ),
    example: '62020 Loss on Disposal',
  },
  ONBOARDING_ADJUSTMENT: {
    label: 'Onboarding adjustment',
    description: (
      <Box>
        <Typography variant="subtitle1" marginBottom={1}>
          Records differences between Hardfin computed balances and general
          ledger balances during initial true-up onboarding.
        </Typography>
        <Typography variant="body1" fontStyle="italic" marginBottom={1}>
          Normal balance: varies
        </Typography>
        <Typography variant="body1">
          Appears in the following journal entries:
        </Typography>
        <List
          dense
          sx={{
            listStyleType: 'disc',
            listStylePosition: 'outside',
            paddingLeft: LIST_ITEM_PADDING,
          }}
        >
          <ListItem sx={{ display: 'list-item', padding: 0 }}>
            Onboarding
          </ListItem>
        </List>
      </Box>
    ),
    example: '99010 Asset Subledger - Onboarding Adjustment',
  },
};
